import { ReactNode } from "react";

import EntityActions from "./EntityActions";
import EmptyState from "./EmptyState";

export interface EntityColumn<T> {
  /**
   * Column heading.
   */
  header: ReactNode;

  /**
   * Render a cell.
   */
  render(item: T): ReactNode;

  /**
   * Optional Tailwind width.
   */
  width?: string;

  /**
   * Optional alignment.
   */
  align?: "left" | "center" | "right";
}

export interface EntityTableProps<T> {
  items: T[];

  columns: EntityColumn<T>[];

  /**
   * Return a unique key.
   */
  getKey(item: T): React.Key;

  /**
   * Empty message.
   */
  emptyMessage?: string;

  /**
   * Enable edit button.
   */
  onEdit?(item: T): void;

  /**
   * Enable delete button.
   */
  onDelete?(item: T): void;

  /**
   * Optional row click.
   */
  onRowClick?(item: T): void;
}

export default function EntityTable<T>({
  items,
  columns,
  getKey,
  emptyMessage = "No records found.",
  onEdit,
  onDelete,
  onRowClick,
}: EntityTableProps<T>) {

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">

      <table className="min-w-full divide-y divide-slate-200">

        <thead className="bg-slate-50">

          <tr>

            {columns.map((column, index) => (
              <th
                key={index}
                className={`
                  px-4 py-3
                  text-left
                  text-sm
                  font-semibold
                  text-slate-700
                  ${column.width ?? ""}
                `}
              >
                {column.header}
              </th>
            ))}

            {(onEdit || onDelete) && (
              <th className="w-32 px-4 py-3 text-right text-sm font-semibold text-slate-700">
                Actions
              </th>
            )}

          </tr>

        </thead>

<tbody className="divide-y divide-slate-100 bg-white">
  {items.length > 0 ? (
    items.map(item => (
      <tr
        key={getKey(item)}
        onClick={() => onRowClick?.(item)}
        className={onRowClick ? "cursor-pointer hover:bg-slate-50" : ""}
      >
        {columns.map((column, index) => (
          <td
            key={index}
            className={`
              px-4 py-3 text-sm text-slate-700
              ${
                column.align === "center"
                  ? "text-center"
                  : column.align === "right"
                  ? "text-right"
                  : "text-left"
              }
            `}
          >
            {column.render(item)}
          </td>
        ))}

        {(onEdit || onDelete) && (
          <td className="px-4 py-3">
            <div className="flex justify-end">
              <EntityActions
                onEdit={() => onEdit?.(item)}
                onDelete={() => onDelete?.(item)}
              />
            </div>
          </td>
        )}
      </tr>
    ))
  ) : (
    <tr>
      <td
        colSpan={columns.length + (onEdit || onDelete ? 1 : 0)}
        className="py-10"
      >
        <EmptyState message={emptyMessage} />
      </td>
    </tr>
  )}
</tbody>

      </table>

    </div>
  );
}